import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DOCUMENT } from '@angular/common';
import { AuthService } from '../Auth/AuthService';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private currentLanguage = new BehaviorSubject<string>('en'); // default language
  currentLanguage$ = this.currentLanguage.asObservable();

  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService
  ) {
    this.initLanguage(); // set default language class on load
  }

  private initLanguage() {
    const savedLang = localStorage.getItem('lang') || 'en'; // default to English
    this.applyLanguage(savedLang);
  }

  changeLanguage(lang: string) {
    this.applyLanguage(lang);
    this.persistLanguage(lang);
  }

  // Pulls the client's saved language preference from the backend (call on dashboard load)
  // so the choice follows them across sessions/devices instead of resetting to English.
  syncLanguageFromBackend() {
    if (!this.authService.isLoggedIn()) {
      return;
    }

    this.authService.getAll('GET_ME').subscribe({
      next: (response: any) => {
        // auth/me returns the user row directly (unwrapped).
        const lang = response?.language;
        if ((lang === 'en' || lang === 'ar') && lang !== this.currentLanguage.value) {
          this.applyLanguage(lang);
        }
      },
      error: () => {
        console.warn('Could not load saved language preference.');
      },
    });
  }

  private applyLanguage(lang: string) {
    localStorage.setItem('lang', lang);
    this.translate.use(lang);
    this.currentLanguage.next(lang);
    this.updateBodyClass(lang);
  }

  // Persists the user's language choice so transactional emails (password reset,
  // subscription, contest updates, etc.) go out in their chosen language.
  private persistLanguage(lang: string) {
    if (!this.authService.isLoggedIn()) {
      return; // switcher also renders on public/unauthenticated pages
    }

    this.authService.updateLanguage(lang).subscribe({
      error: () => {
        console.warn('Could not save language preference to the backend.');
      },
    });
  }

  private updateBodyClass(lang: string) {
    const body = this.document.body;
    body.classList.remove('english-font', 'arabic-font'); // remove previous font classes

    if (lang === 'ar') {
      body.classList.add('arabic-font');
    } else {
      body.classList.add('english-font');
    }
  }
}
